import { v4 as uuidv4 } from 'uuid';
import { MeetingNotification, NotificationType } from '../interfaces/interface';
import { NotificationService } from '../services/NotificationService';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { logger } from 'src/logger';

interface NotifyParams {
  type: NotificationType;
  actorLogin: string;
  actorName: string;
  creatorAlias: string;
  meetingId: string;
  meetingSubject: string;
  slotId?: string;
  slotLabel?: string;
  voteDirection?: 'up' | 'down';
  commentPreview?: string;
}

/**
 * Fire-and-forget notification to all meeting participants.
 * Looks up authorizedLogins from the meeting record and writes
 * a notification to each participant (excluding the actor).
 */
export async function NotifyMeetingParticipants(params: NotifyParams): Promise<void> {
  const { creatorAlias, actorLogin, meetingId } = params;

  const notification: Omit<MeetingNotification, 'id'> = {
    type: params.type,
    actorLogin: params.actorLogin,
    actorName: params.actorName,
    meetingId: params.meetingId,
    meetingSubject: params.meetingSubject,
    meetingCreator: params.creatorAlias,
    slotId: params.slotId,
    slotLabel: params.slotLabel,
    voteDirection: params.voteDirection,
    commentPreview: params.commentPreview?.slice(0, 100),
    createdAt: new Date().toISOString(),
    isRead: false,
    isMarkRead: false
  };

  try {
    // Look up all authorized logins for this meeting
    const meeting = await EmployeeSettingsService.getMeeting(creatorAlias, meetingId);
    const recipients = meeting?.authorizedLogins ?? [creatorAlias];

    // Write to each recipient except the actor
    const writes = recipients
      .filter((login) => login.toLowerCase() !== actorLogin.toLowerCase())
      .map((recipient) =>
        NotificationService.addNotification(recipient, { ...notification, id: uuidv4() }).catch((error) => {
          logger.error('Failed to send notification', { error, recipient, type: params.type });
        })
      );

    await Promise.all(writes);
  } catch (error) {
    logger.error('Failed to notify meeting participants', { error, meetingId, type: params.type });
  }
}
